import { useI18n } from "@baton/i18n";
import { WifiOff } from "@baton/icons";
import { Button } from "../../primitives/buttons/Button/Button.tsx";
import { Banner } from "../Notice/Banner.tsx";

/**
 * FR-015-090, § Offline, § Problems to surface (503 `unavailable`): the banner with its
 * Retry now, the queued-write count, and the "Baton is restarting" variant.
 *
 * Taken from Spectrum UI's Animated Alert (`@spectrumui/alert-1`, Apache-2.0) through `Banner`,
 * which states what differs from the source, and why (2026-09-18, slice (f)): the strip fades down
 * into place and its glyph springs upright as the source's tile does, and the shape is the hard red
 * strip across the page's top the owner decided on 2026-09-18 rather than the source's panel. Retry
 * now states its own text colour, because the secondary Button inherits its host's and the strip's is
 * the white of the red solid. On a narrow window the message wraps and Retry now drops under it
 * (2026-10-24).
 */
export interface OfflineBannerProps {
  readonly variant?: "offline" | "restarting";
  readonly queuedWrites?: number;
  readonly retrying?: boolean;
  readonly onRetry: () => void;
}

export function OfflineBanner({ variant = "offline", queuedWrites = 0, retrying = false, onRetry }: OfflineBannerProps) {
  const { t } = useI18n();
  return (
    <Banner
      variant={variant}
      role="status"
      tile={<WifiOff aria-hidden="true" />}
      actions={
        <Button size="sm" variant="secondary" className="h-(--chip-height-md) px-2 text-text-primary" loading={retrying} onClick={onRetry}>
          {t("ui.offline.retryNow")}
        </Button>
      }
    >
      {variant === "restarting" ? t("ui.offline.restarting") : t("ui.offline.banner")}
      {queuedWrites > 0 && ` ${t("ui.offline.queued", { count: queuedWrites })}`}
    </Banner>
  );
}
